import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  Building, 
  FileText, 
  LogOut,
  Menu,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Settings,
  Shield,
  CreditCard,
  CheckSquare,
  Calendar,
  ClipboardCheck,
  FileCheck,
  Database,
  DollarSign,
  Receipt,
  Wallet,
  CalendarDays,
  Wrench,
  Ticket,
  Kanban,
  BookOpen,
  ClipboardList,
  ListChecks,
  Activity,
  Key,
  ChevronDown,
  Eye,
  EyeOff,
  DoorOpen,
  Upload,
  UserCog,
  FileSpreadsheet
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useState, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useDataCache } from '@/hooks/useDataCache';

export default function AppLayout() {
  const { user, logout, isLoading } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const { getCompanyData, cachedData } = useDataCache();
  
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  
  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => 
      prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
    );
  };

  useEffect(() => {
    const menuGroups: { path: string; subPaths: string[] }[] = [
      { path: '/app/finance', subPaths: ['/app/finance/chart-of-accounts', '/app/finance/transactions', '/app/finance/new-entry'] },
      { path: '/app/sales', subPaths: ['/app/catalogos/productos', '/app/catalogos/descuentos', '/app/sales/promotions', '/app/sales/invoices', '/app/sales/tuitions', '/app/sales/estado-cuenta'] },
      { path: '/app/treasury', subPaths: ['/app/treasury/payments', '/app/treasury/corte-caja', '/app/treasury/creditos', '/app/finance/invoicing'] },
      { path: '/app/tickets', subPaths: ['/app/tickets/nuevo', '/app/tickets/mis-tickets', '/app/tickets/gestion'] },
      { path: '/app/evaluation', subPaths: ['/app/evaluation/plans', '/app/evaluation/gradebook'] },
    ];
    const toExpand = menuGroups
      .filter(g => g.subPaths.some(sp => location.pathname.startsWith(sp)))
      .map(g => g.path);
    if (toExpand.length > 0) {
      setExpandedMenus(prev => Array.from(new Set([...prev, ...toExpand])));
    }
  }, [location.pathname]);

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Load company data on component mount and when user changes
  useEffect(() => {
    if (user) {
      getCompanyData();
    }
  }, [user, getCompanyData]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
            <img src="/qcampusone-logo.png" alt="QCampusOne" className="w-12 h-12 object-contain" />
          </div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Get tenant from localStorage or URL, fallback to testcampus
    const currentTenant = localStorage.getItem('currentTenantSlug') || 'testcampus';
    return <Navigate to={`/${currentTenant}/login`} replace />;
  }

  const navItems = [
    { path: '/app/dashboard', label: t('app.dashboard'), icon: LayoutDashboard, requiredPermission: null },
    { path: '/app/executive-dashboard', label: 'Visor Ejecutivo 360', icon: Activity, requiredPermission: 'executive-dashboard:view' },
    { path: '/app/users', label: t('app.users'), icon: Users, requiredPermission: 'users:list' },
    { path: '/app/alumnos', label: 'Alumnos', icon: GraduationCap, requiredPermission: 'alumnos:list' },
    { path: '/app/prospectos', label: 'Prospectos', icon: UserCog, requiredPermission: 'alumnos:list' },
    { path: '/app/catalogos', label: 'Catálogos', icon: Database, requiredPermission: 'periods:list' },
    { path: '/app/control-escolar/planificacion', label: 'Planificación y Horarios', icon: Calendar, requiredPermission: 'school_control:manage_planning' },
    { path: '/app/control-escolar/apertura-cierre', label: 'Apertura y Cierre', icon: DoorOpen, requiredPermission: 'apertura-cierre:view' },
    { path: '/app/docente/tramites', label: 'Servicios Docentes', icon: FileCheck, requiredPermission: 'teachers:list' },
    { path: '/app/docente/agenda', label: 'Agenda Digital Docente', icon: CalendarDays, requiredPermission: 'schedules:teach' },
    { path: '/app/control-escolar/registro-asistencia', label: 'Registro de Asistencia', icon: CheckSquare, requiredPermission: 'school_control:monitor_attendance' },
    { path: '/app/control-escolar/kardex', label: 'Kardex de Clases', icon: ClipboardCheck, requiredPermission: 'school_control:monitor_attendance' },
    { path: '/app/control-escolar/listas', label: 'Listas de Grupos', icon: ClipboardList, requiredPermission: 'company:read' },
    { path: '/app/control-escolar/reportes', label: 'Reportes Institucionales', icon: FileSpreadsheet, requiredPermission: 'school_control:manage_planning' },
    { 
      path: '/app/evaluation', 
      label: 'Evaluación y Calificaciones', 
      icon: BookOpen, 
      requiredPermission: 'evaluation:plans:list',
      subItems: [
        { path: '/app/evaluation/plans', label: 'Planes de Evaluación', requiredPermission: 'evaluation:plans:list' },
        { path: '/app/evaluation/gradebook', label: 'Libro de Calificaciones', requiredPermission: 'gradebook:view' },
      ]
    },
    { path: '/app/control-escolar/tramites-academicos', label: 'Trámites Académicos', icon: FileCheck, requiredPermission: 'procedures:list' },
    { 
      path: '/app/tickets', 
      label: 'Soporte y Mantenimiento', 
      icon: Wrench, 
      requiredPermissions: ['tickets:create', 'tickets:list', 'tickets:assign', 'tickets:resolve'],
      subItems: [
        { path: '/app/tickets/nuevo', label: 'Reportar Incidencia', requiredPermission: 'tickets:create' },
        { path: '/app/tickets/mis-tickets', label: 'Mis Tickets', requiredPermissions: ['tickets:create', 'tickets:list-own'] },
        { path: '/app/tickets/gestion', label: 'Gestión de Tickets', requiredPermissions: ['tickets:list', 'tickets:assign', 'tickets:resolve'] },
      ]
    },
    { path: '/app/roles', label: 'Roles y Permisos', icon: Shield, requiredPermission: 'roles:list' },
    { path: '/app/tasks', label: 'Gestión de Tareas', icon: CheckSquare, requiredPermissions: ['tasks:list', 'tasks:assign', 'tasks:create'] },
    { path: '/app/tareas-pendientes', label: 'Tareas Pendientes', icon: ListChecks, requiredPermissions: ['company:read', 'alumno360:read-own-profile'] },
    { path: '/app/treasury/subir-comprobante', label: 'Subir Comprobante', icon: Upload, requiredPermission: 'payment-proofs:upload' },
    { path: '/app/administration', label: 'Validación de Pagos', icon: CreditCard, requiredPermission: 'admin:validate-payment' },
    { 
      path: '/app/finance', 
      label: 'Finanzas', 
      icon: DollarSign, 
      requiredPermission: 'finance:accounts:list',
      subItems: [
        { path: '/app/finance', label: 'Empresas', requiredPermission: 'finance:accounts:list' },
        { path: '/app/finance/chart-of-accounts', label: 'Catálogo de Cuentas', requiredPermission: 'finance:accounts:list' },
        { path: '/app/finance/transactions', label: 'Pólizas Contables', requiredPermission: 'finance:transactions:list' },
        { path: '/app/finance/new-entry', label: 'Nueva Póliza', requiredPermission: 'finance:transactions:create' },
      ]
    },
    { 
      path: '/app/sales', 
      label: 'Ventas', 
      icon: Receipt, 
      requiredPermission: 'sales:invoices:list',
      subItems: [
        { path: '/app/catalogos/productos', label: 'Productos y Servicios', requiredPermission: 'sales:products:list' },
        { path: '/app/catalogos/descuentos', label: 'Descuentos', requiredPermission: 'sales:invoices:list' },
        { path: '/app/sales/promotions', label: 'Promociones', requiredPermission: 'promotions:manage' },
        { path: '/app/sales/invoices', label: 'Cuentas de Recibos', requiredPermission: 'sales:invoices:list' },
        { path: '/app/sales/invoices/new', label: 'Nuevo Recibo', requiredPermission: 'sales:invoices:create' },
        { path: '/app/sales/tuitions', label: 'Generar Colegiaturas', requiredPermission: 'sales:invoices:create' },
        { path: '/app/sales/estado-cuenta', label: 'Estado de Cuenta', requiredPermission: 'treasury:payments:list' },
      ]
    },
    { 
      path: '/app/treasury', 
      label: 'Tesorería', 
      icon: Wallet, 
      requiredPermission: 'treasury:payments:list',
      subItems: [
        { path: '/app/treasury/payments', label: 'Pagos Recibidos', requiredPermission: 'treasury:payments:list' },
        { path: '/app/treasury/corte-caja', label: 'Corte de Caja', requiredPermission: 'treasury:cash-cuts:list' },
        { path: '/app/treasury/creditos', label: 'Saldos a Favor', requiredPermission: 'treasury:payments:list' },
        { path: '/app/finance/invoicing', label: 'Facturación Electrónica', requiredPermission: 'cfdi:invoices:list' },
        { path: '/app/cfdi/sat-catalogos', label: 'Catálogos SAT', requiredPermission: 'cfdi:settings:read' },
      ]
    },
    { path: '/app/admin', label: 'Configuración General', icon: Settings, requiredPermission: 'apikeys:list' },
    { path: '/app/company', label: t('app.company'), icon: Building, requiredPermission: 'company:read' },
    { path: '/app/audit', label: t('app.audit'), icon: FileText, requiredPermission: 'audit:read' },
  ];

  // Helper function to check if user has any of the required permissions
  const hasAnyPermission = (requiredPermission?: string | null, requiredPermissions?: string[]) => {
    // If no permission required, allow access
    if (!requiredPermission && (!requiredPermissions || requiredPermissions.length === 0)) return true;

    // The backend's rbac() middleware already lets any 'Admin'-role user through regardless
    // of what's in the permissions table (see backend/src/middleware/auth.ts). Mirror that
    // bypass here so the sidebar never hides a module an Admin can already reach by URL —
    // otherwise a gap in the permissions table (e.g. a new permission not yet backfilled to
    // every Admin role) silently hides a working feature instead of just being cosmetic.
    if (user.role === 'Admin') return true;

    const userPerms = user.permissions || [];
    
    // Check single permission
    if (requiredPermission && userPerms.includes(requiredPermission)) return true;
    
    // Check if user has ANY of the required permissions (OR logic)
    if (requiredPermissions && requiredPermissions.length > 0) {
      return requiredPermissions.some(perm => userPerms.includes(perm));
    }
    
    return false;
  };

  // Handle password change
  const handleChangePassword = async () => {
    setPasswordError('');
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Todos los campos son requeridos');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordError('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    
    try {
      setChangingPassword(true);
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setPasswordError(data.error || 'Error al cambiar contraseña');
        return;
      }
      
      alert('Contraseña actualizada exitosamente');
      setChangePasswordModalOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError('Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  // Filter menu items based on user permissions
  const visibleNavItems = navItems.filter(item => {
    return hasAnyPermission(item.requiredPermission, (item as any).requiredPermissions);
  });

  // Hide sidebar if no menu items are visible
  const showSidebar = visibleNavItems.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu overlay */}
      {showSidebar && sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-black/25" />
        </div>
      )}

      {/* Sidebar */}
      {showSidebar && (
        <div className={cn(
        "fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out",
        // Mobile behavior
        "lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        // Desktop collapse behavior
        sidebarCollapsed ? "lg:w-16" : "lg:w-64",
        // Mobile width is always full when open
        "w-64"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo and Collapse Button */}
          <div className="flex items-center justify-between px-4 py-4 border-b">
            {!sidebarCollapsed && (
              <div className="flex items-center">
                <img 
                  key={cachedData.company?.logoUrl || "default"} 
                  src={cachedData.company?.logoUrl || "/qcampusone-logo.png"} 
                  alt="QCampusOne" 
                  className="h-20 w-30 object-contain rounded"
                />
              </div>
            )}
            {sidebarCollapsed && (
              <div className="mx-auto">
                <img 
                  key={cachedData.company?.logoUrl || "default-collapsed"} 
                  src={cachedData.company?.logoUrl || "/qcampusone-logo.png"} 
                  alt="QCampusOne" 
                  className="h-20 w-30 object-contain rounded"
                />
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 ml-2"
              title={sidebarCollapsed ? "Expandir menú" : "Contraer menú"}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const hasSubItems = 'subItems' in item && item.subItems && item.subItems.length > 0;
              const isExpanded = expandedMenus.includes(item.path);
              const isActive = location.pathname === item.path || 
                (hasSubItems && item.subItems?.some(sub => location.pathname === sub.path));
              
              if (hasSubItems) {
                const visibleSubItems = item.subItems?.filter(sub => 
                  hasAnyPermission(sub.requiredPermission, (sub as any).requiredPermissions)
                ) || [];
                
                if (visibleSubItems.length === 0) return null;
                
                return (
                  <div key={item.path}>
                    <button
                      onClick={() => toggleMenu(item.path)}
                      className={cn(
                        "w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors group relative",
                        isActive
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                        sidebarCollapsed ? "justify-center" : ""
                      )}
                      title={sidebarCollapsed ? item.label : undefined}
                    >
                      <Icon className={cn("h-5 w-5", !sidebarCollapsed && "mr-3")} />
                      {!sidebarCollapsed && (
                        <>
                          <span className="flex-1 text-left">{item.label}</span>
                          <ChevronRight className={cn(
                            "h-4 w-4 transition-transform",
                            isExpanded && "rotate-90"
                          )} />
                        </>
                      )}
                      {sidebarCollapsed && (
                        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                          {item.label}
                        </div>
                      )}
                    </button>
                    {!sidebarCollapsed && isExpanded && (
                      <div className="ml-6 mt-1 space-y-1">
                        {visibleSubItems.map((subItem) => {
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={cn(
                                "flex items-center px-3 py-2 text-sm rounded-md transition-colors",
                                isSubActive
                                  ? "bg-blue-100 text-blue-700 font-medium"
                                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                              )}
                              onClick={() => setSidebarOpen(false)}
                            >
                              {subItem.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors group relative",
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                    sidebarCollapsed ? "justify-center" : ""
                  )}
                  onClick={() => setSidebarOpen(false)}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon className={cn("h-5 w-5", !sidebarCollapsed && "mr-3")} />
                  {!sidebarCollapsed && (
                    <span>{item.label}</span>
                  )}
                  {sidebarCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                      {item.label}
                    </div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="border-t px-2 py-4 relative">
            {!sidebarCollapsed ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="w-full flex items-center justify-between px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user.fullName}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <ChevronDown className={cn("h-4 w-4 text-gray-500 transition-transform", userMenuOpen && "rotate-180")} />
                </button>
                
                {userMenuOpen && (
                  <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        setChangePasswordModalOpen(true);
                      }}
                      className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Key className="h-4 w-4 mr-2" />
                      Cambiar Contraseña
                    </button>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                      }}
                      className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-700">
                    {user.fullName?.charAt(0) || 'U'}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="p-2"
                  title="Cerrar sesión"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Main content */}
      <div className={cn(
        "transition-all duration-300 ease-in-out",
        showSidebar && "lg:pl-64",
        showSidebar && sidebarCollapsed && "lg:pl-16"
      )}>
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white shadow-sm border-b px-4 py-3">
          <div className="flex items-center justify-between">
            {showSidebar && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}
            <div className="lg:hidden flex items-center space-x-3">
              <img 
                key={cachedData.company?.logoUrl || "default-mobile"} 
                src={cachedData.company?.logoUrl || "/qcampusone-logo.png"} 
                alt="QCampusOne" 
                className="h-20 w-30 object-contain rounded"
              />
            </div>
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-gray-900">
                {t('app.welcome')}, {user.fullName}
              </h1>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 w-full">
          <Outlet />
        </main>
      </div>

      {/* Change Password Modal */}
      {changePasswordModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Key className="h-5 w-5 mr-2" />
                Cambiar Contraseña
              </h2>
              <button
                onClick={() => {
                  setChangePasswordModalOpen(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {passwordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                  {passwordError}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña Actual
                </label>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Ingresa tu contraseña actual"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar Nueva Contraseña
                </label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la nueva contraseña"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setChangePasswordModalOpen(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                >
                  {changingPassword ? 'Guardando...' : 'Cambiar Contraseña'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}